import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import DOMPurify from "dompurify";
import { trackImpression, trackClick } from "@/lib/api";

/**
 * AdSlot renders an ad for a given slot type: top_banner | in_feed | sidebar | bottom_sticky.
 * Ads come from /api/ads (fetched once in NewsHome). Supported formats:
 *  - adsense : renders <ins class="adsbygoogle"> and pushes to adsbygoogle
 *  - html    : renders a DOMPurify-sanitized HTML snippet (scripts/handlers stripped)
 *  - image   : renders <img> with a link to link_url
 */
const sanitizeAdHtml = (html) =>
    DOMPurify.sanitize(html || "", {
        FORBID_TAGS: ["script", "style", "form", "input", "textarea", "select", "button", "object", "embed", "link", "meta"],
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "loading", "referrerpolicy", "target"],
    });
const injectAdsenseScript = (client) => {
    if (typeof window === "undefined" || !client) return;
    if (document.querySelector(`script[data-adsense-client="${client}"]`)) return;
    const s = document.createElement("script");
    s.async = true;
    s.crossOrigin = "anonymous";
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
    s.setAttribute("data-adsense-client", client);
    document.head.appendChild(s);
};

const AdContent = ({ ad }) => {
    const rendered = useRef(false);
    useEffect(() => {
        if (rendered.current) return;
        rendered.current = true;
        trackImpression(ad.id);
        if (ad.format === "adsense" && ad.adsense_client) {
            injectAdsenseScript(ad.adsense_client);
            try {
                // @ts-ignore
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
                /* ignore */
            }
        }
    }, [ad.id, ad.format, ad.adsense_client]);

    if (ad.format === "adsense") {
        return (
            <ins
                className="adsbygoogle"
                style={{ display: "block", width: "100%", height: "100%" }}
                data-ad-client={ad.adsense_client}
                data-ad-slot={ad.adsense_slot}
                data-ad-format="auto"
                data-full-width-responsive="true"
            />
        );
    }
    if (ad.format === "html") {
        return (
            <div
                className="w-full h-full"
                dangerouslySetInnerHTML={{ __html: sanitizeAdHtml(ad.content) }}
            />
        );
    }
    // image
    return (
        <a
            href={ad.link_url || "#"}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => trackClick(ad.id)}
            className="block w-full h-full"
        >
            <img
                src={ad.content}
                alt={ad.image_alt || ad.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                    e.currentTarget.style.display = "none";
                }}
            />
        </a>
    );
};

const Placeholder = ({ label }) => (
    <div className="w-full h-full flex items-center justify-center font-sub text-[11px] tracking-[0.24em] uppercase text-black/40">
        {label}
    </div>
);

export const AdSlot = ({ slot, ads = [], testId, className = "", minHeight, hideWhenEmpty = true }) => {
    const list = (ads || []).filter((a) => a.active !== false);
    const ad = list.length ? list[0] : null;
    if (!ad && hideWhenEmpty) return null;
    const heightMap = {
        top_banner: minHeight || 96,
        in_feed: minHeight || 140,
        sidebar: minHeight || 260,
        bottom_sticky: minHeight || 72,
    };
    const height = heightMap[slot] || 100;
    return (
        <div
            className={`ad-slot ${className}`}
            style={{ minHeight: height }}
            data-testid={testId || `ad-slot-${slot}`}
            data-ad-slot-type={slot}
        >
            {ad ? <AdContent ad={ad} /> : <Placeholder label={`${slot.replace(/_/g, " ")} · your ad here`} />}
        </div>
    );
};

export const BottomStickyAd = ({ ads = [] }) => {
    const [dismissed, setDismissed] = React.useState(false);
    const active = (ads || []).filter((a) => a.active !== false);
    if (dismissed || active.length === 0) return null;
    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-black/10 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.15)]"
            data-testid="bottom-sticky-ad"
        >
            <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3">
                <div className="flex-1">
                    <AdSlot slot="bottom_sticky" ads={ads} className="border-0" minHeight={56} hideWhenEmpty={false} />
                </div>
                <button
                    aria-label="Dismiss ad"
                    onClick={() => setDismissed(true)}
                    className="p-1.5 rounded-full hover:bg-black/5 text-black/60"
                    data-testid="bottom-sticky-close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default AdSlot;
